import { PageTemplateView } from "@loot-os/design-system/templates/shared/PageTemplateView";
import { Navigate, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { UsernameDialog } from "../components/UsernameDialog.js";
import { qk } from "../lib/queries.js";
import { queryClient } from "../lib/queryClient.js";
import { useShopper } from "../lib/shopper.js";
import { useTenant } from "../lib/tenant.js";
import {
  type ForgeBuilderEdit,
  useForgeBuilderContent,
  useForgeBuilderHeading,
  useForgeEditTarget,
  useUsernameGate,
} from "../templates/pages/forge/useForgeBuilderContent.js";
import { storefrontTemplateOf } from "../templates/registry.js";

/**
 * PUT /v1/forge/boxes/{boxId}/drop-table: the builder, loaded with the published version. Only
 * the shopper's own live box can be edited; anything else says why.
 */
export function ForgeEditPage() {
  const { boxId } = useParams({ from: "/forge/$boxId/edit" });
  const { user, authPending } = useShopper();
  const { config } = useTenant();
  const [usernameOpen, setUsernameOpen] = useState(false);
  const gate = useUsernameGate({ open: usernameOpen, onOpenChange: setUsernameOpen });
  const target = useForgeEditTarget(boxId);
  const heading = useForgeBuilderHeading("edit", false);

  if (!authPending && !user) return <Navigate to="/login" replace />;
  const notice = gate ?? target.notice;
  if (notice || !target.edit) {
    return (
      <>
        <PageTemplateView
          template={storefrontTemplateOf(config).forgeBuilder}
          content={{ heading, notice: notice ?? { status: "loading" } }}
        />
        {usernameOpen && (
          <UsernameDialog open={usernameOpen} onOpenChange={setUsernameOpen} username={null} />
        )}
      </>
    );
  }
  // A newer version remounts the builder with its rates.
  return <ForgeEditor key={target.edit.version} edit={target.edit} />;
}

function ForgeEditor({ edit }: { edit: ForgeBuilderEdit }) {
  const { config } = useTenant();
  const { content, usernameDialog } = useForgeBuilderContent({
    edit,
    onVersionChanged: () => {
      void queryClient.invalidateQueries({ queryKey: qk.box(edit.boxId) });
      void queryClient.invalidateQueries({ queryKey: qk.myForge });
    },
  });
  return (
    <>
      <PageTemplateView template={storefrontTemplateOf(config).forgeBuilder} content={content} />
      {usernameDialog.open && <UsernameDialog {...usernameDialog} username={null} />}
    </>
  );
}
